import { isNil } from "lodash-es";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import { configDatabaseRecordType, configIdType, IDatabaseRecord, idType, IId } from "@/core/api";
import { attachmentType, IAttachment } from "@/data/attachments";
import { ICodelist } from "@/data/codelists";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { ILocation, locationType } from "@/data/locations";
import { IPhone, phoneType } from "@/data/phones";

export enum AddressType {
  POSTAL = "postal",
  INVOICE = "invoice",
}

export interface ICompanyDto {
  countryCode: ICodelist["code"];
  location: ILocation | null;
  name: string;
  orgNo: string | null;
  vatNo: string | null;
  size: ICodelist["code"] | null;
  searchableProfiles: boolean;
  validVatNumber: boolean;
  validOrgNumber: boolean;
  companyWebsite: string | null;
}

export interface IAddress {
  id?: number;
  addressLine1: string;
  addressLine2: string;
  zipCode: string;
  state: string;
  city: ILocation;
  type: string;
}

export const addressType: TType<IAddress> = {
  id: numberType(),
  addressLine1: stringType(""),
  addressLine2: stringType(""),
  zipCode: stringType(""),
  state: stringType(""),
  city: locationType,
  type: stringType(""),
};

export const companyAddress = ensure(addressType);
export const companyAddressArray = ensureArray(companyAddress);

export interface ICompanyContact {
  id?: number;
  supplierContactPhone: IPhone | null;
  financeContactPhone: IPhone;
  supplierEmail: string;
  supplierContactPhonePrefix?: string;
  financeContactPhonePrefix?: string;
  financeEmail: string;
}

const contactType: TType<ICompanyContact> = {
  id: numberType(),
  supplierContactPhone: phoneType,
  financeContactPhone: phoneType,
  financeEmail: stringType(""),
  supplierContactPhonePrefix: stringType(""),
  financeContactPhonePrefix: stringType(""),
  supplierEmail: stringType(""),
};

export const companyContactType = ensure(contactType);

export interface IPaymentType {
  id?: number;
  name: string;
  countryCode: string;
  isAllCountries: boolean;
  paymentTypeId: number;
  isBankNumberRequired: boolean;
  isSwiftRequired: boolean;
  isIbanRequired: boolean;
}

const paymentType: TType<IPaymentType> = {
  id: numberType(),
  paymentTypeId: numberType(),
  name: stringType(""),
  countryCode: stringType(""),
  isAllCountries: booleanType(),
  isBankNumberRequired: booleanType(),
  isSwiftRequired: booleanType(),
  isIbanRequired: booleanType(),
};

const companyPaymentType = ensure(paymentType);
export const companyPaymentTypeArray = ensureArray(companyPaymentType);

export interface ISupplierBankingDetails {
  id?: number;
  iban?: string;
  swift?: string;
  status: string;
  primary?: boolean;
  companyId?: number;
  bankNumber: string;
  countryCode: string;
  paymentName: string;
  paymentTypeId: number;
  factoringCompany?: boolean;
}

const bankingDetailType: TType<ISupplierBankingDetails> = {
  id: numberType(),
  primary: booleanType(),
  companyId: numberType(),
  paymentTypeId: numberType(),
  iban: stringType(""),
  swift: stringType(""),
  status: stringType(""),
  bankNumber: stringType(""),
  countryCode: stringType(""),
  paymentName: stringType(""),
  factoringCompany: booleanType(false),
};

export const companyBankingDetailType = ensure(bankingDetailType);
export const companyBankingDetailTypeArray = ensureArray(companyBankingDetailType);

export interface ICompanyInsurance {
  id?: number;
  companyId?: number;
  file?: IAttachment;
  expirationDate: string;
  amount?: number;
  accepted: boolean;
  hasInsurance?: boolean;
}

const insuranceType: TType<ICompanyInsurance> = {
  id: numberType(),
  expirationDate: stringType(""),
  amount: numberType(0),
  accepted: booleanType(),
  hasInsurance: booleanType(),
  file: attachmentType,
};

export const companyInsuranceType = ensure(insuranceType);

export interface IRequestToJoinCompanyDto {
  countryCode: ICompanyDto["countryCode"];
  orgNo: string | null;
  vatNo: string | null;
}

export interface IRequestToJoinCompanyCompany extends IDatabaseRecord {
  logo: IAttachment | null;
  name: string;
  vatNo: string | null;
  countryCode: ICompanyDto["countryCode"];
}

export const configRequestToJoinCompanyCompanyType: TType<IRequestToJoinCompanyCompany> = {
  ...configDatabaseRecordType,
  logo: v => (isNil(v) ? null : attachmentType(v)),
  name: stringType(""),
  vatNo: stringType(null),
  countryCode: stringType(null),
};

export const requestToJoinCompanyCompanyType = ensure(configRequestToJoinCompanyCompanyType);

export type TRequestToJoinType = "ACCEPTED" | "REJECTED" | "PENDING";

export interface IRequestToJoinCompany extends IId, IRequestToJoinCompanyDto {
  consultantProfile: IConsultantProfile;
  company: IRequestToJoinCompanyCompany;
  status: TRequestToJoinType;
}

export const configRequestToJoinCompanyType: TType<IRequestToJoinCompany> = {
  ...configIdType,
  company: requestToJoinCompanyCompanyType,
  consultantProfile: idType,
  countryCode: stringType(null),
  orgNo: stringType(null),
  status: stringType(""),
  vatNo: stringType(null),
};

export const requestToJoinCompanyType = ensure(configRequestToJoinCompanyType);

export interface ICompany extends ICompanyDto, IDatabaseRecord {
  consultantProfile: IConsultantProfile;
  logo: IAttachment | null;
  phone: IPhone | null;
  size: string;
  guid: string;
  companyWebsite: string;
}

export const configCompanyType: TType<ICompany> = {
  ...configDatabaseRecordType,
  consultantProfile: idType,
  countryCode: stringType(null),
  location: v => (isNil(v) ? null : locationType(v)),
  logo: v => (isNil(v) ? null : attachmentType(v)),
  name: stringType(""),
  orgNo: stringType(null),
  phone: v => (isNil(v) ? null : phoneType(v)),
  vatNo: stringType(null),
  size: stringType(null),
  searchableProfiles: booleanType(true),
  validVatNumber: booleanType(true),
  guid: stringType(null),
  validOrgNumber: booleanType(true),
  companyWebsite: stringType(null),
};

export const companyType = ensure(configCompanyType);

export interface ICompanyWithCompleteness extends ICompany {
  companyProfileCompleteness: number;
}

export const configCompanyWithCompletenessType: TType<ICompanyWithCompleteness> = {
  ...configCompanyType,
  companyProfileCompleteness: numberType(0),
};

export const companyWithCompletenessType = ensure(configCompanyWithCompletenessType);

export interface ICreateCompanyValidation {
  exists: boolean;
  validVatNo: boolean;
  validOrgNo: boolean;
}

export const configCreateCompanyValidationType: TType<ICreateCompanyValidation> = {
  exists: booleanType(false),
  validVatNo: booleanType(true),
  validOrgNo: booleanType(true),
};

export const createCompanyValidationType = ensure(configCreateCompanyValidationType);
